import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { workflowAPI } from '../../lib/api';
import { PRODUCT_TYPES, errMsg } from '../../lib/workflow';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { TennisPriceInputs } from '../listings/TennisPriceInputs';
import { isTennis as isTennisType, IGI_UPCHARGE, TENNIS_CONFIG, tennisPricesComplete, tennisPricesPayload } from '../../lib/tennis';

const NOTE_IDEAS = ['White gold hero shot 🤍', 'Show it on the hand ✋', 'Add a 360° video 🎬', 'Match the reference colours 🎨'];

export const SubmitLinkCard = ({ onSubmitted }) => {
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [ptype, setPtype] = useState('engagement_ring');
  const [specialty, setSpecialty] = useState(false);
  const [tennisPrices, setTennisPrices] = useState({});
  const isTennis = isTennisType(ptype);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);

  const submit = async (force = false) => {
    const u = url.trim();
    if ((u.match(/https?:\/\//g) || []).length > 1) { toast.error('One link at a time please 🙏'); return; }
    if (!/^https?:\/\/\S+$/.test(u)) { toast.error('Paste a product link starting with https://'); return; }
    if (isTennis && !tennisPricesComplete(tennisPrices, ptype)) { toast.error('Enter the Etsy price for every carat 💎'); return; }
    setBusy(true);
    try {
      const r = await workflowAPI.submitLink({
        url: u, notes, product_type: ptype, is_specialty_cut: specialty && !isTennis, force,
        ...(isTennis ? { tennis_prices: tennisPricesPayload(tennisPrices, ptype) } : {}),
      });
      toast.success(`Link #${r.data.today_count} for today is in 🎉 Nivedit's on it!`);
      setUrl(''); setNotes(''); setDup(null); setTennisPrices({});
      onSubmitted?.(r.data);
    } catch (e) {
      if (e.response?.status === 409) setDup(e.response.data.detail);
      else toast.error(errMsg(e, 'Could not submit the link'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wf-card p-6 space-y-4 h-full" data-testid="nayan-submit-card">
      <div>
        <h2 className="font-jakarta text-lg font-bold text-[#2D2623]">🔗 Submit a listing link</h2>
        <p className="text-xs text-[#9A8E85] mt-0.5">One link at a time. We auto-create the listing and send it to Nivedit for visuals.</p>
      </div>
      <Input
        value={url}
        onChange={(e) => { setUrl(e.target.value); setDup(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder="https://www.etsy.com/listing/…"
        className="h-12 rounded-2xl border-[#E8E1D5] bg-[#FDFBF7] text-sm focus-visible:ring-[#E07A5F]"
        data-testid="nayan-submit-url-input"
      />
      <div className="flex flex-wrap gap-2">
        {PRODUCT_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setPtype(t.value)}
            data-testid={`product-type-chip-${t.value}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-[background-color,transform] duration-150 active:scale-95 ${
              ptype === t.value ? 'bg-[#2D2623] text-white border-[#2D2623]' : 'bg-white text-[#6E635B] border-[#E8E1D5] hover:bg-[#F7F3EC]'
            }`}
          >
            {t.emoji} {t.label}
          </button>
        ))}
        {!isTennis && <button
          type="button"
          onClick={() => setSpecialty((v) => !v)}
          data-testid="specialty-cut-toggle"
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
            specialty ? 'bg-[#FDF0EC] text-[#C85A32] border-[#F6D5CB]' : 'bg-white text-[#9A8E85] border-dashed border-[#E8E1D5]'
          }`}
        >
          {specialty ? '💠 Specialty cut ($250/ct)' : '💠 Specialty cut?'}
        </button>}
      </div>
      {isTennis && (
        <div className="rounded-2xl border border-[#E8E1D5] bg-[#FDFBF7] p-4 space-y-2.5" data-testid="nayan-tennis-prices">
          <p className="text-xs font-semibold text-[#2D2623]">💎 Etsy price per carat (non-certified, final)</p>
          <TennisPriceInputs productType={ptype} value={tennisPrices} onChange={setTennisPrices} testIdPrefix="nayan-tennis-price" inputClassName="rounded-xl border-[#E8E1D5] bg-white" />
          <p className="text-[11px] text-[#9A8E85]">IGI certified adds +${IGI_UPCHARGE} automatically · 14K white & yellow gold {TENNIS_CONFIG[ptype].priceNote && `· ${TENNIS_CONFIG[ptype].priceNote}`}</p>
        </div>
      )}
      <div className="space-y-2">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="📝 Notes for Nivedit (optional): angles, metal, reference style…"
          className="rounded-2xl border-[#E8E1D5] bg-[#FDFBF7] text-sm resize-none focus-visible:ring-[#E07A5F]"
          data-testid="nayan-notes-input"
        />
        <div className="flex flex-wrap gap-1.5">
          {NOTE_IDEAS.map((n) => (
            <button key={n} type="button" onClick={() => setNotes((v) => (v ? `${v}\n${n}` : n))} className="text-[11px] rounded-full bg-[#F7F3EC] px-2.5 py-1 text-[#6E635B] hover:bg-[#F0EAE1]">
              + {n}
            </button>
          ))}
        </div>
      </div>
      {dup && (
        <div className="rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-xs text-[#92400E] space-y-2" data-testid="nayan-duplicate-warning">
          <p>⚠️ {dup}</p>
          <Button size="sm" variant="outline" onClick={() => submit(true)} className="h-7 rounded-xl text-xs" data-testid="nayan-submit-anyway-btn">Submit anyway</Button>
        </div>
      )}
      <Button
        onClick={() => submit(false)}
        disabled={busy}
        className="w-full h-12 rounded-2xl bg-[#E07A5F] hover:bg-[#D0694E] text-white text-sm font-bold transition-transform active:scale-[0.98]"
        data-testid="nayan-submit-link-button"
      >
        {busy ? <><Loader2 size={15} className="mr-2 animate-spin" /> Creating listing…</> : '🚀 Submit link to Nivedit'}
      </Button>
    </div>
  );
};
